import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { Input } from "antd"
import { Modal } from "components/ui/modals/Modal/Modal"
import { ButtonPrimary } from "components/ui/buttons/ButtonPrimary/ButtonPrimary"
import { ButtonSecondary } from "components/ui/buttons/ButtonSecondary/ButtonSecondary"
import { SearchGlassIcon } from "components/ui/icons/input/SearchGlassIcon"
import {
	useCreateCustomModelNumber,
	useGetCustomModelNumbers,
} from "hooks/api/customModelNumbers/customModelNumbers.hook"
import { useApiErrorFormatter } from "hooks/apiErrorFormatter.hook"
import { computeMatch } from "helpers/modelNumberMatch.helper"
import { CustomModelNumberRow } from "./CustomModelNumberRow"
import { CustomModelNumberModal } from "components/pages/PageAllFiles/layout/FileInfo/layout/CustomModelNumbers/CustomModelNumberModal"
import type { TCustomModelNumber, TCustomModelNumberPayload } from "types/customModelNumbers.type"
import type { TDimensions } from "types/modelNumbers.type"
import style from "./SelectCustomModelNumberModal.module.scss"

const {
	body,
	section,
	sectionTitle,
	dimensionsRow,
	dimensionLabel,
	unset,
	seriesValue,
	list,
	stateBlock,
	stateTitle,
	stateSubtitle,
	footer,
	footerButtons,
	footerButton,
	createButton,
	search,
	searchIcon,
} = style

type Props = {
	isOpen: boolean
	onClose: () => void
	drawingId: number | null
	/** The panel's currently-typed dimensions — what the match badges score rows against. */
	dimensions: { width: number | null; height: number | null; depth: number | null }
	/**
	 * The quotation's configured series (`configuration.series_id`, resolved to its display name) —
	 * read-only context for the pick, not something this flow sets. `null` when the quotation has
	 * none configured yet.
	 */
	seriesName: string | null
	initialSelectedId: number | null
	onSave: (model: TCustomModelNumber) => void
}

const renderDimension = (label: string, value: number | null) => (
	<span>
		<span className={dimensionLabel}>{label}</span>
		{value === null ? <span className={unset}>Unset</span> : <span>{value}</span>}
	</span>
)

/** Adapts a custom model number's scalar W/H/D into the range-string shape `computeMatch` expects. */
const toDimensionsInput = (model: TCustomModelNumber): TDimensions => ({
	width: [String(model.width)],
	height: [String(model.height)],
	depth: [String(model.depth)],
	avg_size: "",
})

/**
 * "Select custom model number" picker for the "add custom object" flow (#1371) — deliberately its OWN
 * component rather than a mode grafted onto `SelectModelNumberModal`: this picker's data source
 * (the drawing's custom model numbers) and behavior (no scope tabs — only ever one pool, per the
 * issue: "the selector offers only the Custom tab") are different enough that reusing the REQ-398
 * component would mean threading a third, mostly-unrelated mode through its already-intricate
 * scope/tab logic. Converting an EXISTING object to custom, where all three tabs stay available
 * (#1346), is a separate, not-yet-built piece of work.
 *
 * "Create custom model number" opens the management screen's own `CustomModelNumberModal`
 * (#1345/#1384) in its create mode (`customModelNumber={null}`) rather than a copy of it — the two
 * forms are identical, and duplicating one just to swap where it portals to would drift the moment
 * either side changed a field. This flow owns the mutation itself (the shared component takes
 * `isPending`/`onSave` rather than calling the endpoint on its own), so a successful create can
 * both refetch this picker's list and select the new row.
 */
export const SelectCustomModelNumberModal = ({
	isOpen,
	onClose,
	drawingId,
	dimensions,
	seriesName,
	initialSelectedId,
	onSave,
}: Props) => {
	const { data: customModelNumbers = [] } = useGetCustomModelNumbers(drawingId ?? 0, isOpen)
	const queryClient = useQueryClient()
	const { formatError } = useApiErrorFormatter()
	const { mutate: createCustomModelNumber, isPending: isCreatePending } = useCreateCustomModelNumber()
	const [term, setTerm] = useState("")
	const [selectedId, setSelectedId] = useState<number | null>(initialSelectedId)
	const [isCreateOpen, setIsCreateOpen] = useState(false)
	const anchorRef = useRef<HTMLDivElement | null>(null)
	const portalRef = useRef<HTMLDivElement | null>(null)
	const createAnchorRef = useRef<HTMLDivElement | null>(null)
	const createPortalRef = useRef<HTMLDivElement | null>(null)

	useEffect(() => {
		if (!isOpen) return
		setSelectedId(initialSelectedId)
		setTerm("")
	}, [isOpen, initialSelectedId])

	useEffect(() => {
		return () => {
			portalRef.current?.remove()
			portalRef.current = null
			createPortalRef.current?.remove()
			createPortalRef.current = null
		}
	}, [])

	const getContainer = useCallback((): HTMLElement => {
		if (portalRef.current?.isConnected) return portalRef.current
		const root = anchorRef.current?.getRootNode()
		const host: ShadowRoot | HTMLElement =
			root instanceof ShadowRoot
				? root
				: ((document.querySelector("apryse-webviewer") as HTMLElement | null)?.shadowRoot ?? document.body)
		const container = document.createElement("div")
		container.setAttribute("data-portal", "select-custom-model-number-modal")
		host.appendChild(container)
		portalRef.current = container
		return container
	}, [])

	// A second, independent portal for the nested "Create custom model number" modal — it opens on
	// top of this one and needs its own container the same way `getContainer` above does.
	const getCreateContainer = useCallback((): HTMLElement => {
		if (createPortalRef.current?.isConnected) return createPortalRef.current
		const root = createAnchorRef.current?.getRootNode()
		const host: ShadowRoot | HTMLElement =
			root instanceof ShadowRoot
				? root
				: ((document.querySelector("apryse-webviewer") as HTMLElement | null)?.shadowRoot ?? document.body)
		const container = document.createElement("div")
		container.setAttribute("data-portal", "create-custom-model-number-modal")
		host.appendChild(container)
		createPortalRef.current = container
		return container
	}, [])

	const handleCreate = useCallback(
		(payload: TCustomModelNumberPayload) => {
			if (drawingId === null) return
			createCustomModelNumber(
				{ drawingId, payload },
				{
					onSuccess: ({ data }) => {
						queryClient.invalidateQueries({ queryKey: ["customModelNumbers", drawingId] })
						setSelectedId(data.id)
						setIsCreateOpen(false)
					},
					onError: formatError,
				},
			)
		},
		[drawingId, createCustomModelNumber, queryClient, formatError],
	)

	const filtered = useMemo(() => {
		const q = term.trim().toLowerCase()
		if (!q) return customModelNumbers
		return customModelNumbers.filter(
			(model) => model.model_number.toLowerCase().includes(q) || model.name.toLowerCase().includes(q),
		)
	}, [customModelNumbers, term])

	const ranked = useMemo(
		() =>
			filtered
				.map((model) => ({ model, match: computeMatch(toDimensionsInput(model), dimensions) }))
				.sort((a, b) => a.match.outOfRangeCount - b.match.outOfRangeCount),
		[filtered, dimensions],
	)

	const selected = useMemo(() => customModelNumbers.find((model) => model.id === selectedId) ?? null, [customModelNumbers, selectedId])
	const canSave = selected !== null

	const handleSave = () => {
		if (!selected) return
		onSave(selected)
		onClose()
	}

	return (
		<>
			<div ref={anchorRef} style={{ display: "none" }} aria-hidden />
			<Modal
				isModalOpen={isOpen}
				setModalClose={onClose}
				width={640}
				title="Select custom model number"
				getContainer={getContainer}
			>
				<div className={body}>
					<div className={section}>
						<span className={sectionTitle}>Cabinet dimensions</span>
						<div className={dimensionsRow}>
							{renderDimension("W: ", dimensions.width)}
							{renderDimension("H: ", dimensions.height)}
							{renderDimension("D: ", dimensions.depth)}
						</div>
					</div>

					<div className={section}>
						<span className={sectionTitle}>Series</span>
						{seriesName ? (
							<span className={seriesValue}>{seriesName}</span>
						) : (
							<span className={unset}>Not set in quotation configuration</span>
						)}
					</div>

					<div className={search}>
						<Input
							placeholder="Search by name, description or price"
							prefix={<SearchGlassIcon className={searchIcon} />}
							size="large"
							value={term}
							onChange={(e) => setTerm(e.target.value)}
						/>
					</div>

					<div className={list}>
						{ranked.length === 0 ? (
							<div className={stateBlock}>
								<span className={stateTitle}>
									{customModelNumbers.length === 0 ? "No custom model numbers yet" : `No model numbers match “${term.trim()}”`}
								</span>
								<span className={stateSubtitle}>
									{customModelNumbers.length === 0
										? "Create one for this drawing to use it here."
										: "Clear the search to see the whole list again."}
								</span>
							</div>
						) : (
							ranked.map(({ model, match }) => (
								<CustomModelNumberRow
									key={model.id}
									model={model}
									match={match}
									isSelected={selectedId === model.id}
									onSelect={setSelectedId}
								/>
							))
						)}
					</div>

					<div className={footer}>
						<span className={createButton}>
							<ButtonSecondary type="button" submittable={drawingId !== null} onClick={() => setIsCreateOpen(true)}>
								Create custom model number
							</ButtonSecondary>
						</span>
						<div className={footerButtons}>
							<ButtonSecondary type="button" className={footerButton} onClick={onClose}>
								Cancel
							</ButtonSecondary>
							<ButtonPrimary className={footerButton} submittable={canSave} onClick={handleSave}>
								Save
							</ButtonPrimary>
						</div>
					</div>
				</div>
			</Modal>
			<div ref={createAnchorRef} style={{ display: "none" }} aria-hidden />
			<CustomModelNumberModal
				customModelNumber={null}
				isOpen={isCreateOpen}
				isPending={isCreatePending}
				onClose={() => setIsCreateOpen(false)}
				onSave={handleCreate}
				getContainer={getCreateContainer}
			/>
		</>
	)
}
